/**
 * Contrato da assinatura digital embutida no arquivo (E74.F4, ADR-0075 §3).
 *
 * Espelha apps/api/types/exame_eletronico_assinatura_digital.py. Módulo próprio
 * pela mesma razão que o backend separou o seu: o contrato do exame já ocupa o
 * teto de 400 linhas do handbook §7, e esta evidência cresce por conta própria.
 * Quem importava estes tipos de `lib/exame_eletronico` continua importando — a
 * re-exportação de lá é o contrato público.
 */
import type { ItemVerificacaoData } from '@/lib/exame_eletronico';

/** Assinatura digital embutida no próprio arquivo (E74.F4; revogação na E76.F1). */
export type ChaveAssinaturaDigital =
  | 'assinatura_digital_presente'
  | 'assinatura_digital_integridade'
  | 'assinatura_digital_abrangencia'
  | 'assinatura_digital_cadeia'
  | 'assinatura_digital_revogacao'
  | 'assinatura_digital_signatario'
  | 'assinatura_digital_carimbo';

/**
 * O que os passes de revogação apuraram para UMA assinatura, no material que o
 * próprio arquivo embute (E76.F1). `revogado` é sempre o certificado do
 * signatário; `autoridade_revogada` é uma autoridade intermediária da cadeia —
 * achados distintos, nunca um pelo outro.
 */
export type SituacaoRevogacao =
  | 'nao_revogado_no_material'
  | 'revogado'
  | 'autoridade_revogada'
  | 'nao_conferivel';

/**
 * O que o registro persistido comprova sobre o PASSE de revogação
 * (F-E76.F3-06). Três fatos distintos, que o quadro não pode confundir:
 * `sem_item` — o item nunca foi persistido (exame anterior à E76.F1);
 * `nao_executado` — o item existe e declara que a conferência não rodou;
 * `executado` — o passe rodou, e só aqui o estado individual por assinatura é
 * fato pericial, não default de schema.
 */
export type SituacaoPasseRevogacao = 'executado' | 'nao_executado' | 'sem_item';

/**
 * Espelho de `situacao_do_passe_de_revogacao` do backend — fonte única para
 * decidir se o passe de revogação existiu e rodou. A mera presença da chave
 * não basta: `situacao === 'nao_executado'` é o próprio estágio dizendo que
 * não conferiu nada (F-E76.F3-06).
 */
export function situacaoDoPasseDeRevogacao(itens: ItemVerificacaoData[]): SituacaoPasseRevogacao {
  const item = itens.find((i) => i.chave === 'assinatura_digital_revogacao');
  if (!item) return 'sem_item';
  return item.situacao === 'nao_executado' ? 'nao_executado' : 'executado';
}

/**
 * Veredito SUBORDINADO do estágio de assinatura digital (E76.F4, ADR-0076) —
 * vocabulário decidido pelo owner, em linguagem de laudo. Calculado no backend
 * por função pura dos itens agregados; a UI só renderiza, nunca recalcula.
 * `null`/ausente no resultado é o exame anterior ao veredito — nenhuma
 * alegação.
 */
export type VereditoAssinaturaDigital =
  | 'confirmada'
  | 'nao_confirmada'
  | 'nao_foi_possivel_confirmar'
  | 'nao_se_aplica';

/**
 * Confiabilidade do carimbo do tempo de UMA assinatura (E76.F2; quarto estado
 * na correção F-E76.F2-02). `confiavel` — o carimbo é íntegro/válido e a
 * cadeia da autoridade carimbadora (TSA) fechou até uma raiz de confiança;
 * `autoridade_nao_confirmada` — o carimbo é íntegro/válido, mas a cadeia não
 * fechou (não é, por si, prova de fraude — RESERVADO a este caso);
 * `token_invalido` — havia carimbo, mas ele próprio não é criptograficamente
 * íntegro/válido (achado distinto e mais grave, contradição verificada);
 * `ausente` — não havia carimbo.
 */
export type SituacaoCarimbo =
  | 'confiavel'
  | 'autoridade_nao_confirmada'
  | 'token_invalido'
  | 'ausente';

/** O certificado que assinou, como o laudo o cita. */
export interface CertificadoSignatarioData {
  titular: string;
  emissor: string;
  numero_serie: string;
  impressao_digital: string;
  valido_de?: string | null;
  valido_ate?: string | null;
  cpf?: string | null;
  /** e-CNPJ do certificado de pessoa jurídica da ICP-Brasil (E76.F3) — é o que
   * rotula a assinatura da empresa como o que ela é. */
  cnpj?: string | null;
}

/** Uma assinatura digital encontrada no arquivo (E74.F4). */
export interface AssinaturaEmbutidaData {
  campo: string;
  integra: boolean;
  confiavel: boolean;
  abrange_o_arquivo: boolean;
  alterada_apos_assinada: boolean;
  assinado_em?: string | null;
  carimbo_em?: string | null;
  certificado?: CertificadoSignatarioData | null;
  motivo_nao_confiavel?: string | null;
  revogacao: SituacaoRevogacao;
  revogado_em?: string | null;
  /**
   * `null` é o marcador de LEGADO (E76.F2): o item `assinatura_digital_carimbo`
   * já existia antes desta Fase, então um registro antigo com carimbo presente
   * carrega este campo como `null` — a UI só mostra a linha de confiabilidade
   * quando ele vem preenchido.
   */
  carimbo_confiabilidade?: SituacaoCarimbo | null;
  carimbo_autoridade?: string | null;
  algoritmo_hash?: string | null;
  mecanismo_assinatura?: string | null;
  /**
   * Papel do signatário na doutrina por papel (E76.F3): `true` — o certificado
   * traz o CPF do caso, e os itens agregados do quadro são calculados sobre
   * esta assinatura; `false` — terceiro identificado; `null`/ausente — não
   * identificável OU registro anterior à Fase, e a UI não mostra atribuição
   * que o exame não fez (padrão F-E76.F1-04).
   */
  do_titular?: boolean | null;
}

/**
 * O que o estágio de assinatura digital apurou do próprio arquivo (E74.F4).
 * `revogacao_em_linha` fica no registro porque o laudo declara COMO se conferiu.
 * `validado_em` é o instante de referência da validação (E76.F1) — `null` nos
 * registros anteriores à Fase, que também não trazem o item de revogação em
 * `itens`: é por essa ausência que o quadro detecta o exame histórico.
 */
export interface AssinaturaDigitalApuradaData {
  itens: ItemVerificacaoData[];
  assinaturas: AssinaturaEmbutidaData[];
  ancoras_confiaveis: number;
  /**
   * E76.F5 — identificador estável do conjunto de âncoras aplicado ao exame
   * (`sha256:<hex>:<n>`), calculado no backend. `null`/ausente é o registro
   * anterior à Fase.
   */
  trust_store_id?: string | null;
  revogacao_em_linha: boolean;
  validado_em?: string | null;
}
